/**
 * @fileoverview Header component for the application.
 * It includes the app logo, navigation to the accounts page, and a theme toggle button.
 */
import React from 'react';
import { SunIcon, MoonIcon, UserCircleIcon, ArrowRightOnRectangleIcon } from './icons';
import type { User } from '../types';
import { Tooltip } from './Tooltip';

interface HeaderProps {
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  onNavigate: (page: 'main' | 'controlPanel') => void;
  currentUser: User;
  onLogout: () => void;
}

export const Header: React.FC<HeaderProps> = ({ 
  theme, 
  toggleTheme, 
  onNavigate, 
  currentUser, 
  onLogout
}) => {
  const stringToColor = (str: string) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xFF;
        color += ('00' + value.toString(16)).substr(-2);
    }
    return color;
  };
  
  return (
    <header className="bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm sticky top-0 z-20 border-b border-slate-200 dark:border-gray-700">
      <div className="container mx-auto px-4 md:px-6 py-6 md:py-7 flex flex-wrap md:flex-nowrap justify-between items-center gap-4">
        <div className="flex items-center gap-6">
          {/* Logo and App Name */}
          <button onClick={() => onNavigate('main')} className="flex items-center flex-shrink-0" aria-label="Go to main page">
            <div className="h-16 sm:h-20 w-44 sm:w-56">
              <span className="ac-logo" role="img" aria-label="AmplifyChat logo" />
            </div>
          </button>
        </div>

        <div className="flex items-center gap-2">
          <Tooltip text="Dashboard">
            <button
              onClick={() => onNavigate('main')}
              className="flex items-center gap-2 px-3 py-2 rounded-full text-slate-600 dark:text-gray-300 hover:bg-slate-200 dark:hover:bg-gray-700 transition-colors"
            >
              <span className="text-sm font-semibold">Dashboard</span>
            </button>
          </Tooltip>
          <Tooltip text="Settings & Accounts">
            <button
              onClick={() => onNavigate('controlPanel')}
              className="flex items-center gap-2 p-2 rounded-full text-slate-500 dark:text-gray-400 hover:bg-slate-200 dark:hover:bg-gray-700 transition-colors"
            >
              <UserCircleIcon className="w-6 h-6" />
              <span className="text-sm font-medium hidden sm:inline">Control Panel</span>
            </button>
          </Tooltip>
          
          <Tooltip text={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}>
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full text-slate-500 dark:text-gray-400 hover:bg-slate-200 dark:hover:bg-gray-700 transition-colors"
            >
              {theme === 'light' ? <MoonIcon className="w-6 h-6" /> : <SunIcon className="w-6 h-6" />}
            </button>
          </Tooltip>

          <div className="flex items-center gap-3 border-l border-slate-200 dark:border-gray-700 pl-3 ml-1">
             <div 
                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                style={{ backgroundColor: stringToColor(currentUser.name) }}
              >
                {currentUser.name.charAt(0).toUpperCase()}
              </div>
            <span className="text-sm font-medium hidden md:inline">Hello, {currentUser.name}</span>
            <Tooltip text="Logout" position="bottom">
              <button
                onClick={onLogout}
                className="p-2 rounded-full text-slate-500 dark:text-gray-400 hover:bg-slate-200 dark:hover:bg-gray-700 transition-colors"
              >
                <ArrowRightOnRectangleIcon className="w-6 h-6" />
              </button>
            </Tooltip>
          </div>
        </div>
      </div>
    </header>
  );
};
